import { Badge, type BadgeProps } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { DUE_STATUS_COLORS, SEVERITY_COLORS, VALIDITY_COLORS } from '../theme';
import type { AircraftStatus, DueStatus, ValidityStatus } from '../api/types';

const AIRCRAFT_STATUS_COLORS: Record<string, string> = {
  AIRWORTHY: 'teal',
  IN_MAINTENANCE: 'yellow',
  GROUNDED: 'red',
  STORED: 'gray',
  SOLD: 'gray',
};

const FLIGHT_STATE_COLORS: Record<string, string> = {
  DRAFT: 'gray',
  FILED: 'blue',
  CERTIFIED: 'teal',
  CORRECTED: 'grape',
};

const EVENT_STATUS_COLORS: Record<string, string> = {
  DRAFT: 'gray',
  OPEN: 'blue',
  DONE: 'teal',
  CANCELLED: 'gray',
};

const ALERT_STATE_COLORS: Record<string, string> = {
  ACTIVE: 'red',
  ACKNOWLEDGED: 'blue',
  SNOOZED: 'gray',
  RESOLVED: 'teal',
};

const PALETTES = {
  aircraft: AIRCRAFT_STATUS_COLORS,
  due: DUE_STATUS_COLORS as Record<string, string>,
  validity: VALIDITY_COLORS as Record<string, string>,
  severity: SEVERITY_COLORS as Record<string, string>,
  flight: FLIGHT_STATE_COLORS,
  event: EVENT_STATUS_COLORS,
  alertState: ALERT_STATE_COLORS,
} as const;

/** Where in the translation catalog each palette's labels live. */
const NAMESPACES: Record<keyof typeof PALETTES, string> = {
  aircraft: 'aircraft',
  due: 'maintenance',
  validity: 'staff',
  severity: 'alerts',
  flight: 'logbook',
  event: 'maintenance',
  alertState: 'alerts',
};

export type StatusKind = keyof typeof PALETTES;

/**
 * The single place a status becomes a colour.
 *
 * Maintenance due states, document validity and alert severities all use the same traffic
 * light, so a red badge means the same thing wherever it appears.
 */
export function StatusBadge({
  kind,
  value,
  label,
  ...props
}: {
  kind: StatusKind;
  value: AircraftStatus | DueStatus | ValidityStatus | string | null | undefined;
  label?: string;
} & Omit<BadgeProps, 'color' | 'children'>) {
  const { t } = useTranslation();
  if (!value) return null;
  const color = PALETTES[kind][value] ?? 'gray';
  const key = `${NAMESPACES[kind]}.${value}`;
  const translated = t(key);
  return (
    <Badge color={color} {...props}>
      {label ?? (translated === key ? value : translated)}
    </Badge>
  );
}
